import { platformDetail, platformIconSrc, platformParts } from "./platformicons";
import { drawSkullMark } from "./sprites";
import { longPress } from "./touch";
import type { PlayerRow, SpellView } from "./types";
import { mmss } from "./ui";
import { viewGlyph } from "./viewicons";

/**
 * The Players panel: everybody in the world, their levels, and who is
 * wearing a skull and for how long (asked for on 2026-09-27: "show a panel
 * with online players and their levels, and skull status").
 *
 * It is the Who list kept up rather than asked for. The server pushes a
 * fresh list whenever what this reader would see changes — somebody
 * arriving or leaving, a level, a skull going up, coming off or being
 * pushed along — and never merely because a second went by, so the
 * seconds on a skull are counted down here, on this page's own clock, from
 * the number that came with the list.
 */

/** One row of the panel as it reads at a given moment. */
export interface PlayerLine {
  name: string;
  /**
   * "L12", and "L12 dwarf" once they have chosen a race (2026-10-10:
   * "abbreviate Level 30 to L30 in the players panel").
   */
  level: string;
  /** What is left on their skull's timer as m:ss, or "" for no skull. */
  skull: string;
  /** Whether they wear a skull: only a kill's timer is one (2026-10-24). */
  skulled: boolean;
  /**
   * The browser and system they play on, "Firefox on Windows", or "" when
   * it is not known (2026-10-06: "Show what OS platform/browser people are
   * on in the players list").
   */
  platform: string;
  /**
   * On your own row, while you are off everybody else's list, what the
   * shut eye beside your name says; "" on every other row (2026-10-07: "if
   * a character is hidden from the player list, show an icon on the player
   * list so they know they are hidden").
   */
  hidden: string;
  me: boolean;
}

/**
 * The rows as they read `elapsed` seconds after the list arrived. A skull
 * whose count has run out here is shown as no skull, though the list that
 * says so is at most a tick behind: the count was rounded up on the way
 * out, so this side reaches nought no sooner than the skull really ends.
 */
export function playerLines(rows: PlayerRow[], me: string, elapsed: number): PlayerLine[] {
  const gone = Math.max(0, Math.floor(elapsed));
  return rows.map((r) => {
    const left = (r.skull ?? 0) - gone;
    const level = r.race ? `L${r.level} ${r.race}` : `L${r.level}`;
    return { name: r.name, level, skull: left > 0 ? mmss(left) : "", skulled: left > 0, platform: r.platform ?? "", hidden: r.hidden ?? "", me: r.name === me };
  });
}

/**
 * The mark drawn beside a skulled name on the map, made once into a picture
 * the rows can share. It is drawn at twice its size and shown at half, so
 * a high-density screen gets a sharp one.
 */
let skullIcon = "";
function skullSrc(): string {
  if (skullIcon) return skullIcon;
  const cv = document.createElement("canvas");
  cv.width = cv.height = 24;
  const ctx = cv.getContext("2d");
  if (!ctx) return "";
  ctx.scale(2, 2);
  drawSkullMark(ctx, 6, 6.6, 10);
  skullIcon = cv.toDataURL();
  return skullIcon;
}

/**
 * The chat box as a row of the panel needs it (2026-10-07: "make it so
 * clicking a player's name in the list puts it into the chat field if
 * currently active"): whether it is open for typing, and putting a name
 * into it at the caret.
 */
export interface ChatField {
  active(): boolean;
  put(name: string): void;
}

/**
 * value with word put in where the caret is, from start to end (a
 * selection is replaced), spaced so it reads as a word: a space before it
 * unless it starts the line or follows one, and a space after it unless
 * one is already there, so the next word can be typed straight on. caret
 * is where the cursor goes, after the word and its space.
 */
export function insertWord(value: string, start: number, end: number, word: string): { value: string; caret: number } {
  const a = Math.max(0, Math.min(start, value.length));
  const b = Math.max(a, Math.min(end, value.length));
  const before = value.slice(0, a);
  const after = value.slice(b);
  const lead = before === "" || /\s$/.test(before) ? "" : " ";
  const put = `${lead}${word}`;
  if (/^\s/.test(after)) return { value: before + put + after, caret: a + put.length + 1 };
  return { value: `${before}${put} ${after}`, caret: a + put.length + 1 };
}

/** What putInChat needs of the input: an HTMLInputElement has all of it. */
export interface ChatInput {
  value: string;
  selectionStart: number | null;
  selectionEnd: number | null;
  maxLength: number;
  setSelectionRange(start: number, end: number): void;
  focus(): void;
}

/**
 * Put name into the chat box at its caret and keep the box focused, so
 * typing carries straight on. A name that would take the line past what
 * the box holds is left out, rather than the box holding more than it
 * lets anybody type.
 */
export function putInChat(chat: ChatInput, name: string) {
  const end = chat.value.length;
  const got = insertWord(chat.value, chat.selectionStart ?? end, chat.selectionEnd ?? end, name);
  if (!(chat.maxLength > 0 && got.value.length > chat.maxLength)) {
    chat.value = got.value;
    chat.focus();
    chat.setSelectionRange(got.caret, got.caret);
    return;
  }
  chat.focus();
}

/**
 * The spells a name on the panel offers (2026-10-07: "right clicking or
 * long-pressing a players name in the player list allows to cast blood
 * bond or find person from a menu"): both said at somebody by name. The
 * words and the price are the server's (see spells.go) and are what the
 * menu shows when this character does not hold the spell yet, since the
 * panel lists only the spells your pool can hold; a held spell's own view
 * is used otherwise, which is what it costs you where you stand.
 */
export const NAME_SPELLS = [
  { name: "blood bond", words: "gwedh", mana: 30 },
  { name: "find person", words: "tiro", mana: 20 },
] as const;

/** One spell on the name menu, as main.ts puts it on the menu. */
export interface NameSpell {
  label: string;
  /** What is said into the world: the words and the name, as typed. */
  text: string;
  title: string;
  disabled: boolean;
}

/**
 * The two spells as they stand for somebody called name, greyed the way
 * the Spells panel colours them: not held yet (the level is too low for
 * the price), refused by the server (not enough mana, or anything else it
 * says), or cooling. serverNow is the server's clock read on this page,
 * or null before there is a reading, when a cooling spell counts as all
 * of its wait still to go. Taking a blood bond off whoever carries it is
 * free and never refused, as the player menu has it.
 */
export function nameSpells(spells: SpellView[] | undefined, name: string, bondwith: string, serverNow: number | null): NameSpell[] {
  return NAME_SPELLS.map((s) => {
    const sp = spells?.find((v) => v.name.toLowerCase() === s.name);
    const words = sp?.words ?? s.words;
    const text = `${words} ${name}`;
    if (s.name === "blood bond" && sp && bondwith === name) {
      return { label: "Take the blood bond off — free", text, title: `Say the words at ${name} again: their wounds are their own, and it costs nothing`, disabled: false };
    }
    const label = `Cast ${s.name} — ${sp?.mana ?? s.mana} mana`;
    if (!sp) {
      return { label, text, title: `You do not hold ${s.name} yet: it takes ${s.mana} mana, more than your pool holds at your level.`, disabled: true };
    }
    if (sp.why) return { label, text, title: sp.why, disabled: true };
    const left = sp.ready && sp.cool ? (serverNow === null ? sp.cool : Math.min(sp.cool, sp.ready - serverNow)) : 0;
    if (left > 0) {
      const secs = Math.ceil(left / 1000);
      return { label, text, title: `${sp.name} is cooling: ready in ${secs} ${secs === 1 ? "second" : "seconds"}.`, disabled: true };
    }
    return { label, text, title: `Say “${text}”. ${sp.look}`, disabled: false };
  });
}

const SKULL_TITLE =
  "Wearing a skull, for the time shown: they killed somebody who was not protection zone locked. They stay locked while it runs, so no protection zone will take them, and attacking them locks you out of nothing.";

/**
 * How long after a press its click still counts as made with the chat box
 * open: a tap is well inside it, and a hold long enough to outlast it is a
 * long press, whose click is swallowed.
 */
const ARMED_MS = 1500;

/** Whether a press landed on a mark that answers a long press itself. */
function ownPress(target: EventTarget | null): boolean {
  const cell = (target as Element | null)?.closest?.(".pplat, .phide");
  return !!cell && cell.childElementCount > 0;
}

export class PlayersPanel {
  private rows: PlayerRow[] = [];
  /** When the rows arrived, on the page's clock, in milliseconds. */
  private at = 0;
  private timer: ReturnType<typeof setInterval> | null = null;
  /** Each row's skull cell, in the order of rows, for the countdown to rewrite. */
  private cells: { box: HTMLElement; time: HTMLElement }[] = [];

  /**
   * `me` is the reader's own name, whose row is picked out the way the Who
   * list picks it out; `pick` is a click on somebody else's row.
   */
  constructor(
    private readonly body: HTMLElement,
    private readonly me: () => string,
    private readonly pick: (name: string, level: string) => void,
    /** Shows a row's platform in full, for a long press: a finger has no hover. */
    private readonly detail?: (name: string, text: string) => void,
    /**
     * The chat box, whose being open turns a click on a name into typing
     * it; and the menu a right-click or a long press on somebody else's
     * name opens, with the line their row reads under the name.
     */
    private readonly more: { chat?: ChatField; menu?: (name: string, level: string) => void } = {},
  ) {}

  /** A list from the server, which replaces whatever the panel was showing. */
  set(rows: PlayerRow[], now: number) {
    this.rows = rows;
    this.at = now;
    this.draw();
    // The countdown runs only while somebody on the list has a skull: a
    // timer ticking over a list with nothing to count is a timer for
    // nothing. A second is the grain of the m:ss it rewrites.
    const counting = rows.some((r) => (r.skull ?? 0) > 0);
    if (counting && !this.timer) this.timer = setInterval(() => this.tick(performance.now()), 1000);
    if (!counting && this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }

  /**
   * Everybody else on the list, in its order (tallest first), for a spell
   * said at somebody anywhere in the world to offer.
   */
  others(): string[] {
    const me = this.me();
    return this.rows.filter((r) => r.name !== me).map((r) => r.name);
  }

  /** The session is over: nobody is in a world you are not in. */
  clear() {
    this.set([], 0);
  }

  private lines(now: number) {
    return playerLines(this.rows, this.me(), (now - this.at) / 1000);
  }

  private draw() {
    const lines = this.lines(this.at);
    this.cells = [];
    const out: HTMLElement[] = lines.map((l) => {
      // Your own row is not a button: the menu a row opens is for doing
      // something to somebody else.
      const row = document.createElement(l.me ? "div" : "button");
      row.className = l.me ? "prow me" : "prow";
      if (row instanceof HTMLButtonElement) {
        row.type = "button";
        row.title = `${l.name}: click for what you can do to them, or the spells said at them by name when they are out of sight; right-click or press and hold for those spells`;
      }
      this.wireClick(row, l.name, l.me ? undefined : (n) => this.pick(n, l.level));
      // Spells said at them by name, from wherever they are (2026-10-07:
      // "right clicking or long-pressing a players name in the player list
      // allows to cast blood bond or find person from a menu"). The whole
      // row is the target, as it is for the click, except the marks that
      // have a long press of their own: a press there is theirs. Your own
      // row has no menu, there being nothing to say at yourself.
      if (!l.me && this.more.menu) {
        const menu = this.more.menu;
        longPress(row, () => menu(l.name, l.level), (ev) => !ownPress(ev.target));
      }
      const name = document.createElement("span");
      name.className = "pname";
      name.textContent = l.name;
      const box = document.createElement("span");
      box.className = "pskull";
      box.title = SKULL_TITLE;
      const icon = document.createElement("img");
      icon.alt = "Skull";
      // A skull only ever arrives with a fresh list, so a row drawn
      // without one never needs the picture.
      if (l.skulled) icon.src = skullSrc();
      const time = document.createElement("span");
      box.append(icon, time);
      const level = document.createElement("span");
      level.className = "plevel";
      level.textContent = l.level;
      // A shut eye on your own row while you are off everybody else's
      // list (2026-10-07: "if a character is hidden from the player list,
      // show an icon on the player list so they know they are hidden"):
      // your row is the one row that is always there for you, which is
      // why it needs saying. The server sends the words, which say which
      // way you went off — /hide or /invisible — and how to come back; a
      // hover is the title and a long press opens them, as on the
      // platform marks. Like those, the span is on every row, empty where
      // there is nothing to say, so the rows keep the same five cells.
      const hide = document.createElement("span");
      hide.className = "phide";
      if (l.hidden) {
        hide.append(viewGlyph("hidden"));
        hide.title = l.hidden;
        hide.setAttribute("role", "img");
        hide.setAttribute("aria-label", l.hidden);
        if (this.detail) {
          const show = this.detail;
          longPress(hide, () => show(l.name, l.hidden));
        }
      }
      row.append(name, hide, box, level);
      // What they play on, as two small marks after the name, the browser's
      // and the system's (2026-10-07: "use icons for player platforms in
      // the list. chrome, windows, macos, all have icons for example.
      // hovering or long pressing should show more details"). A half with
      // no mark is its word. The hover is the title; a long press opens the
      // same words in the box a held bag square opens, and is swallowed
      // there rather than reaching the row's click.
      // The span is there with or without them, so every row has the same
      // cells and the levels stay in one column.
      const plat = document.createElement("span");
      plat.className = "pplat";
      row.insertBefore(plat, box);
      if (l.platform) {
        const { browser, system } = platformParts(l.platform);
        for (const part of [browser, system]) {
          if (!part) continue;
          const src = platformIconSrc(part);
          if (src) {
            const img = document.createElement("img");
            img.src = src;
            img.alt = part;
            img.draggable = false;
            plat.append(img);
          } else {
            const word = document.createElement("span");
            word.className = "pplatword";
            word.textContent = part;
            plat.append(word);
          }
        }
        const detail = platformDetail(l.name, l.platform);
        plat.title = detail;
        plat.setAttribute("aria-label", detail);
        if (this.detail) {
          const show = this.detail;
          longPress(plat, () => show(l.name, detail));
        }
      }
      this.cells.push({ box, time });
      return row;
    });
    this.body.replaceChildren(...out);
    this.paint(lines);
  }

  /**
   * A click on a row: its pick, or, while the chat box is open for typing,
   * the name typed into it at the caret (2026-10-07: "make it so clicking
   * a player's name in the list puts it into the chat field if currently
   * active"). Whether the box is open is read as the press lands, because
   * by the click it may not be: a mouse button going down on anything but
   * the box takes the focus off it, and a finger's tap can too. The press
   * is kept from doing that while the box is open — a mouse's
   * mousedown is what moves the focus, and refusing it leaves the caret
   * where it was — and the name is put in with the box focused again in
   * any case, which on a phone is what keeps the keyboard up. A click with
   * no press before it, from the keyboard, goes by the box as it is then.
   * Your own row has no pick, but your name still goes in.
   */
  private wireClick(row: HTMLElement, name: string, pick?: (name: string) => void) {
    const chat = this.more.chat;
    let armedAt = 0;
    row.addEventListener("pointerdown", () => {
      armedAt = chat?.active() ? performance.now() : 0;
    });
    row.addEventListener("mousedown", (ev) => {
      if (armedAt || chat?.active()) ev.preventDefault();
    });
    row.addEventListener("click", () => {
      const typing = !!chat && (chat.active() || (armedAt > 0 && performance.now() - armedAt < ARMED_MS));
      armedAt = 0;
      if (typing) chat!.put(name);
      else pick?.(name);
    });
  }

  /** Rewrite the clocks and nothing else, so a focused row keeps its focus. */
  private tick(now: number) {
    this.paint(this.lines(now));
  }

  private paint(lines: PlayerLine[]) {
    lines.forEach((l, i) => {
      const c = this.cells[i];
      if (!c) return;
      c.box.hidden = !l.skulled;
      c.time.textContent = l.skull;
    });
  }
}
